import { AppConfigContext } from '@config/AppConfig';
import { useIsFromCompany } from '@config/config.hooks';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useAllowEndorseDT } from '@transmittal/designerTransmittal/designerTransmittal.hooks';
import { useAllowEndorseST } from '@transmittal/siteTransmittal/siteTransmittal.hooks';
import { ETransmittalStatus, ETransmittalType } from '@transmittal/transmittal.constants';
import {
  IProjectTransmittal,
  IProjectTransmittalDetail,
  IProjectTransmittalDistribution,
} from '@transmittal/transmittal.models';
import {
  EIceTransmittalPermission,
  EPermission,
  EReviewerTransmittalPermission,
  ERolePermission,
  ESiteTransmittalPermission,
  ETransmittalPermission,
} from '@userProfile/permission/permission.constants';
import {
  useFetchTransmittalSiteTransmittalForCheckerOptions,
  useFetchTransmittalSiteTransmittalForReviewerOptions,
} from '@transmittal/transmittal.queries';
import {
  isBelongToCompany,
  isBelongToTeam,
  isGemsSiteUser,
  isPermittedAsSystemUser,
  isPermittedAsSiteOperationUser,
  isRestrictedAccess,
  hasPermissionInArray,
  useIsLambethOrGammonSite,
  useIsViewer,
} from '@userProfile/permission/permission.services';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IMasterScheduleDetail } from '@masterSchedule/detail/masterScheduleDetail.models';
import { useBelongToSiteOperationsCompany, useNominatedInMasterSchedule } from '@masterSchedule/masterSchedule.hooks';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { useAllowEndorseIceT, useAllowNewIceT } from '@transmittal/checkerTransmittal/checkerTransmittal.hooks';
import { useAllowEndorseReview, useAllowNewLir } from '@transmittal/lambethInternalReview/lambethInternalReview.hooks';
import { routes } from '@routes/routes.models';
import { ComponentType, UseFormReturn } from '@gcl/react-core';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { QueriesObserver, useQueryClient } from '@tanstack/react-query';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { IProjectTransmittalItem } from '@transmittal/transmittalItem/transmittalItem.models';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { IFormFileDrawingItem } from '@file/projectFile/projectFile.models';
import { EUserProfilePreferenceAttributeName } from '@userProfile/userProfilePreferences.constants';
import { useUserProfilePreferences } from '@userProfile/userProfilePreferences.hooks';
import {
  EReviewResponseFormStatus,
  ETransmittalConfirmationActionType,
  ETransmittalDetailsFormKeys,
} from './transmittalDetailPage.constants';
import { ITransmittalDetailsFormState } from './transmittalDetailPage.models';
import { getItemsOfUploadRevisionWithSameFileName } from './transmittalItemTableForm/transmittalItemTableForm.hooks';

export const useTransmittalTopBtnsVisibility = (
  useFormMethods: UseFormReturn,
  projectId: number,
  transmittalDetails: IProjectTransmittalDetail | undefined,
  masterSchedule: IMasterScheduleDetail | undefined,
  purposeCode: string | undefined,
  transmittalItemDocumentTypes?: IProjectTransmittalItemDocumentType[],
) => {
  const isEnableSaveTransmittal = useEnableSaveTransmittal(transmittalDetails, masterSchedule);
  const isEmptyTransmittal = useIsEmptyTransmittal(projectId, transmittalDetails, transmittalItemDocumentTypes);
  const isEnableEndorseTransmittal = useEnableEndorseTransmittal(transmittalDetails, masterSchedule);
  const isEnableRequestEndorsement = useEnableRequestEndorsementTransmittal(transmittalDetails, masterSchedule);
  const isEnableReleaseForConstruction = useEnableReleaseForConstruction(
    transmittalDetails,
    masterSchedule,
    purposeCode,
  );

  const isEnablePrepareApproval = useEnablePrepareApproval(projectId, transmittalDetails, masterSchedule);
  const isEnablePrepareReview = useEnablePrepareReview(projectId, transmittalDetails, masterSchedule);
  const isEnableDeleteTransmittal = useEnableDeleteTransmittal(transmittalDetails, masterSchedule);

  return {
    isEnableSaveTransmittal,
    isEnableEndorseTransmittal: isEnableEndorseTransmittal && !isEmptyTransmittal,
    isEnableRequestEndorsement,
    isEnableReleaseForConstruction,
    isEnablePrepareApproval,
    isEnablePrepareReview,
    isEnableDeleteTransmittal,
  };
};

const useEnableSaveTransmittal = (
  transmittalDetails: IProjectTransmittal | undefined,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const { isSrp, isTwc, isTwcm, isNominatedDesigner, isNominatedChecker, isNominatedReviewer } =
    useNominatedInMasterSchedule(masterSchedule);
  if (transmittalDetails?.status !== ETransmittalStatus.DRAFT) return false;

  switch (transmittalDetails.type) {
    case ETransmittalType.DESIGNER:
      return isNominatedDesigner;
    case ETransmittalType.SITE:
      return isSrp || isTwc || isTwcm;
    case ETransmittalType.ICE:
      return isNominatedChecker;
    case ETransmittalType.REVIEWER:
      return isNominatedReviewer;
    default:
      return false;
  }
};

const useEnableDeleteTransmittal = (
  transmittalDetails: IProjectTransmittal | undefined,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const { isSrp, isTwc, isTwcm, isNominatedDesigner, isNominatedChecker, isNominatedReviewer } =
    useNominatedInMasterSchedule(masterSchedule);
  if (transmittalDetails?.status !== ETransmittalStatus.DRAFT) return false;

  switch (transmittalDetails.type) {
    case ETransmittalType.DESIGNER:
      return isNominatedDesigner;
    case ETransmittalType.SITE:
      return isSrp || isTwc || isTwcm;
    case ETransmittalType.ICE:
      return isNominatedChecker;
    case ETransmittalType.REVIEWER:
      return isNominatedReviewer;
    default:
      return false;
  }
};

export const useIsEmptyTransmittal = (
  projectId: number,
  transmittalDetails: IProjectTransmittalDetail | undefined,

  transmittalItemDocumentTypes?: IProjectTransmittalItemDocumentType[],
) => {
  const queryClient = useQueryClient();

  const [isEmptyTransmittal, setIsEmptyTransmittal] = useState(true);

  React.useEffect(() => {
    const obs = transmittalItemDocumentTypes
      ?.filter((type) => type.allowUpload)
      .map((type) => {
        return {
          queryKey: [
            projectId,
            transmittalDetails?.masterScheduleId,
            FETCH_QUERIES_NAME.TRANSMITTAL_ITEMS,
            transmittalDetails?.id,
            type.id,
          ],
        };
      });
    const observer = new QueriesObserver(queryClient, obs);
    const unsubscribe = observer.subscribe((result) => {
      const notEmpty = result.some((r) => ((r.data ?? []) as IProjectTransmittalItem[]).length > 0);
      setIsEmptyTransmittal(!notEmpty);
    });
    return () => {
      unsubscribe();
    };
  }, [transmittalDetails?.masterScheduleId, transmittalDetails?.id, JSON.stringify(transmittalItemDocumentTypes)]);
  return isEmptyTransmittal;
};

export const useEnableEndorseTransmittal = (
  transmittalDetails: IProjectTransmittalDetail | undefined,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const allowEndorseDT = useAllowEndorseDT(masterSchedule);
  const allowEndorseST = useAllowEndorseST(masterSchedule);
  const allowEndorseIceT = useAllowEndorseIceT(masterSchedule);
  const allowEndorseRT = useAllowEndorseReview(masterSchedule);
  if (!transmittalDetails?.type || transmittalDetails.status === ETransmittalStatus.APPROVED) return false;
  if (transmittalDetails?.type === ETransmittalType.DESIGNER) return allowEndorseDT;
  if (transmittalDetails.type === ETransmittalType.SITE) return allowEndorseST;
  if (transmittalDetails.type === ETransmittalType.ICE) return allowEndorseIceT;
  if (transmittalDetails.type === ETransmittalType.REVIEWER) return allowEndorseRT;

  return false;
};

const useEnableRequestEndorsementTransmittal = (
  transmittalDetails: IProjectTransmittal | undefined,
  masterSchedule?: IMasterScheduleDetail,
) => {
  const { isNominatedDesigner, isSrp, isTwc, isTwcm } = useNominatedInMasterSchedule(masterSchedule);

  if (transmittalDetails?.type === ETransmittalType.DESIGNER) {
    return isNominatedDesigner;
  }
  if (transmittalDetails?.type === ETransmittalType.SITE) return isSrp || isTwc || isTwcm;

  return true;
};

const useEnableReleaseForConstruction = (
  transmittalDetails: IProjectTransmittalDetail | undefined,
  masterSchedule: IMasterScheduleDetail | undefined,
  purposeCode: string | undefined,
) => {
  const appConfigs = React.useContext(AppConfigContext);
  const { isTwc } = useNominatedInMasterSchedule(masterSchedule);

  if (!transmittalDetails || !masterSchedule) return false;
  if (!transmittalDetails.canRfc) return false;
  if (!isTwc) return false;
  if (transmittalDetails?.twcReleased || transmittalDetails?.status !== ETransmittalStatus.APPROVED) return false;
  if (transmittalDetails.type === ETransmittalType.DESIGNER) {
    const hasAccessRightToRFC = isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
      EPermission.DESIGNER_TRANSMITTAL_RFC_BY_TWC,
    ]);

    if (!hasAccessRightToRFC) return false;
    if (transmittalDetails.canRfc) return true;
  }
  if (transmittalDetails.type === ETransmittalType.ICE) {
    return (
      transmittalDetails.approvedByProjectUserProfile?.userProfile?.id !== appConfigs.configs.myUserProfile?.id &&
      isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
        EIceTransmittalPermission.RELEASE_FOR_CONSTRUCTION,
      ])
    );
  }
  return false;
};

const useEnablePrepareApproval = (
  projectId: number,
  transmittalDetails: IProjectTransmittal | undefined,
  masterSchedule: IMasterScheduleDetail | undefined,
) => {
  const isPermitted = useAllowNewIceT();
  const { isNominatedChecker } = useNominatedInMasterSchedule(masterSchedule);

  const { data: siteTransmittalOptions } = useFetchTransmittalSiteTransmittalForCheckerOptions(
    { projectId },
    transmittalDetails?.type === ETransmittalType.SITE && transmittalDetails.status === ETransmittalStatus.APPROVED,
  );

  if (!isPermitted) return false;
  if (!isNominatedChecker) return false;
  if (!siteTransmittalOptions) return false;
  return !!siteTransmittalOptions.find((st) => st.id === transmittalDetails?.id);
};

export const useEnablePrepareReview = (
  projectId: number,
  transmittalDetails: IProjectTransmittalDetail | undefined,
  masterSchedule: IMasterScheduleDetail | undefined,
) => {
  const isPermitted = useAllowNewLir();
  const { isNominatedReviewer } = useNominatedInMasterSchedule(masterSchedule);
  const { data: siteTransmittalOptions } = useFetchTransmittalSiteTransmittalForReviewerOptions(
    { projectId },
    transmittalDetails?.type === ETransmittalType.SITE && transmittalDetails.status === ETransmittalStatus.APPROVED,
  );
  if (!isPermitted) return false;
  if (!isNominatedReviewer) return false;
  if (!siteTransmittalOptions) return false;
  return !!siteTransmittalOptions.find((st) => st.id === transmittalDetails?.id);
};

export const useTransmittalSupplementaryInfo = (transmittal: IProjectTransmittal | undefined) => {
  const mode: DetailPageAccessMode = transmittal?.status === ETransmittalStatus.DRAFT ? 'Update' : 'View';
  const fileType = getTransmittalProjectSupplementaryFileType(transmittal?.type);
  return { mode, fileType };
};
const getTransmittalProjectSupplementaryFileType = (type?: ETransmittalType) => {
  switch (type) {
    case ETransmittalType.ICE:
      return EProjectFileType.CHECKER_TRANSMITTAL_SUPPLEMANTARY;
    case ETransmittalType.REVIEWER:
      return EProjectFileType.REVIEWER_TRANSMITTAL_SUPPLEMANTARY;
    case ETransmittalType.SITE:
      return EProjectFileType.SITE_TRANSMITTAL_SUPPLEMANTARY;
    case ETransmittalType.DESIGNER:
    default:
      return EProjectFileType.DESIGNER_TRANSMITTAL_SUPPLEMANTARY;
  }
};

export const useHasCommentAndResponseRight = (
  transmittal: IProjectTransmittal | undefined,
  masterScheduleCheckerId: number | undefined,
  isSuperseded: boolean,
  masterSchedule: IMasterScheduleDetail | undefined,
) => {
  const appConfigs = React.useContext(AppConfigContext);

  const isFromSendFromCompany = useIsFromCompany(transmittal?.sendFromProjectCompanyId || -1);
  const isFromSendToCompany = useIsFromCompany(transmittal?.sendToProjectCompanyId || -1);
  const isLambethOrGammonSite = useIsLambethOrGammonSite();
  const isViewer = useIsViewer();

  const { isSrp, isTwc, isTwcm, isNominatedChecker, isNominatedDesigner, isNominatedReviewer } =
    useNominatedInMasterSchedule(masterSchedule);

  const hasCommentRight = useMemo(() => {
    if (transmittal?.type === ETransmittalType.SITE) {
      return hasPermissionInArray(
        appConfigs.configs.myPermissions,
        ESiteTransmittalPermission.ICE_COMMENT,
        masterSchedule?.responsibleChecker.companyTypeId,
        masterSchedule?.responsibleChecker.organizationTypeId,
      );
    }
    if (transmittal?.type === ETransmittalType.REVIEWER)
      return hasPermissionInArray(
        appConfigs.configs.myPermissions,
        EReviewerTransmittalPermission.RT_COMMENT,
        masterSchedule?.responsibleReviewer?.companyTypeId,
        masterSchedule?.responsibleReviewer?.organizationTypeId,
      );
    return isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ETransmittalPermission.COMMENT]);
  }, [JSON.stringify(transmittal), JSON.stringify(appConfigs.configs.myPermissions), JSON.stringify(masterSchedule)]);

  const isSrpOrTwc = isSrp || isTwc || isTwcm;

  const canCreateComment = React.useMemo(() => {
    if (isSuperseded) return false;
    if (transmittal?.status !== ETransmittalStatus.APPROVED && transmittal?.type !== ETransmittalType.REVIEWER)
      return false;

    switch (transmittal?.type) {
      case ETransmittalType.DESIGNER:
      case ETransmittalType.ICE:
        return hasCommentRight && isSrpOrTwc;
      case ETransmittalType.SITE:
        return hasCommentRight && isNominatedChecker;
      case ETransmittalType.REVIEWER:
        return hasCommentRight && isNominatedReviewer;
      default:
        return false;
    }
  }, [
    JSON.stringify(transmittal),
    masterScheduleCheckerId,
    hasCommentRight,
    isSrpOrTwc,
    isNominatedChecker,
    isNominatedReviewer,
    isFromSendFromCompany,
    isFromSendToCompany,
    isSuperseded,
  ]);

  const hasResponseRight = useMemo(() => {
    if (transmittal?.type === ETransmittalType.SITE)
      return (
        isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [ESiteTransmittalPermission.RESPONSE]) ||
        hasPermissionInArray(
          appConfigs.configs.myPermissions,
          ESiteTransmittalPermission.RESPONSE,
          masterSchedule?.responsibleDesigner?.companyTypeId,
          masterSchedule?.responsibleDesigner?.organizationTypeId,
        )
      );
    if (transmittal?.type === ETransmittalType.REVIEWER) {
      return (
        isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [EReviewerTransmittalPermission.RT_REPLY]) ||
        hasPermissionInArray(
          appConfigs.configs.myPermissions,
          EReviewerTransmittalPermission.RT_REPLY,
          masterSchedule?.responsibleDesigner?.companyTypeId,
          masterSchedule?.responsibleDesigner?.organizationTypeId,
        )
      );
    }
    return (
      isPermittedAsSystemUser(appConfigs.configs.myPermissions, [ESiteTransmittalPermission.RESPONSE]) ||
      hasPermissionInArray(
        appConfigs.configs.myPermissions,
        ETransmittalPermission.RESPONSE,
        masterSchedule?.responsibleDesigner?.companyTypeId,
        masterSchedule?.responsibleDesigner?.organizationTypeId,
      )
    );
  }, [JSON.stringify(transmittal), JSON.stringify(appConfigs.configs.myPermissions), JSON.stringify(masterSchedule)]);

  const canReplyComment = React.useMemo(() => {
    if (isSuperseded) return false;
    if (transmittal?.status !== ETransmittalStatus.APPROVED && transmittal?.type !== ETransmittalType.REVIEWER)
      return false;
    switch (transmittal?.type) {
      case ETransmittalType.DESIGNER:
      case ETransmittalType.ICE:
        return hasResponseRight && isNominatedDesigner;
      case ETransmittalType.SITE:
        return hasResponseRight && (isSrpOrTwc || isNominatedDesigner);
      case ETransmittalType.REVIEWER:
        return hasResponseRight && (isSrpOrTwc || isTwcm || isNominatedDesigner);
      default:
        return false;
    }
  }, [
    JSON.stringify(transmittal),
    hasResponseRight,
    isFromSendFromCompany,
    isSuperseded,
    isSrpOrTwc,
    isTwcm,
    isNominatedDesigner,
  ]);

  const canViewComment = useMemo(() => {
    if (transmittal?.status !== ETransmittalStatus.APPROVED && transmittal?.type !== ETransmittalType.REVIEWER)
      return false;

    return (
      isSrpOrTwc ||
      isNominatedDesigner ||
      isNominatedChecker ||
      isNominatedReviewer ||
      isLambethOrGammonSite ||
      isViewer
    );
  }, [JSON.stringify(transmittal), isSrpOrTwc, isNominatedDesigner, isNominatedChecker, isNominatedReviewer]);

  const isBelongToSiteOperationsCompany = useBelongToSiteOperationsCompany(transmittal?.projectId as number);
  const canViewDraftComment = React.useMemo(() => {
    if (isSuperseded) return false;
    if (transmittal?.status !== ETransmittalStatus.APPROVED && transmittal?.type !== ETransmittalType.REVIEWER)
      return false;

    switch (transmittal?.type) {
      case ETransmittalType.DESIGNER:
        return hasCommentRight && isBelongToSiteOperationsCompany;
      case ETransmittalType.ICE:
        return hasCommentRight && isSrpOrTwc;
      case ETransmittalType.SITE:
        return hasCommentRight && isNominatedChecker;
      case ETransmittalType.REVIEWER:
        return hasCommentRight && isNominatedReviewer;
      default:
        return false;
    }
  }, [
    JSON.stringify(transmittal),
    hasCommentRight,
    isBelongToSiteOperationsCompany,
    isSrpOrTwc,
    isNominatedChecker,
    isNominatedReviewer,
    isSuperseded,
  ]);

  return { canCreateComment, canReplyComment, canViewComment, canViewDraftComment };
};

export const checkGeneralAccessRestrictedTransmittal = ({
  type,
  transmittal,
  masterSchedule,
  distributionData,
}: {
  type?: ETransmittalType;
  transmittal?: IProjectTransmittalDetail;
  masterSchedule?: IMasterScheduleDetail;
  distributionData?: IProjectTransmittalDistribution;
}) => {
  const appConfigs = useContext(AppConfigContext);

  const isBelongToDistributionLocal = isBelongToDistribution(distributionData);
  const isIms = isPermittedAsSystemUser(appConfigs.configs.myPermissions, [
    ERolePermission.IMS_TEAM_OR_SYSTEM_DEVELOPER,
  ]);

  const isGemsSiteUserLocal = isGemsSiteUser();

  const isDesignerCompanyRestricted = isRestrictedAccess(masterSchedule?.responsibleDesigner?.id ?? 0);
  const isCheckerCompanyRestricted = isRestrictedAccess(masterSchedule?.responsibleChecker?.id ?? 0);
  const isDesigner = useIsFromCompany(masterSchedule?.responsibleDesigner?.id ?? 0);
  const isReviewerCompany = useIsFromCompany(masterSchedule?.responsibleReviewer?.id ?? -1);

  if (type === ETransmittalType.DESIGNER) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isDesigner) return false;
      // if (isIms) return false;
    } else {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }

  if (type === ETransmittalType.SITE) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      // if (isGemsSiteUserLocal) return false;
      if (isIms) return false;
      return (
        !isPermittedAsSiteOperationUser(appConfigs.configs.myPermissions, [
          ERolePermission.SITE_ENGINEER,
          ERolePermission.TEMPORARY_WORK_COORDINATOR,
        ]) ?? true
      );
    }
    if (!isDesignerCompanyRestricted) return false;
    if (!isCheckerCompanyRestricted) return false;
    if (isBelongToDistributionLocal) return false;
  }

  if (type === ETransmittalType.ICE) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      const isChecker = isBelongToCompany(
        appConfigs.configs.myCompanies ?? [],
        masterSchedule?.responsibleChecker.id ?? 0,
      );
      const isCheckerTeam = isBelongToTeam(
        appConfigs.configs.myTeams ?? [],
        masterSchedule?.responsibleCheckerTeams.map((t) => t.projectTeamId) ?? [],
      );
      if (isChecker && isCheckerTeam) return false;
      // if (isIms) return false;
    }
    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }

  if (type === ETransmittalType.REVIEWER) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isGemsSiteUserLocal) return false;
      if (isDesigner) return false;
      if (isIms) return false;
      if (isReviewerCompany) return false;
    }

    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }

  return true;
};

export const checkGeneralAccessRestrictedTransmittalHistory = ({
  type,
  transmittal,
  masterSchedule,
  distributionData,
}: {
  type?: ETransmittalType;
  transmittal?: IProjectTransmittalDetail;
  masterSchedule?: IMasterScheduleDetail;
  distributionData?: IProjectTransmittalDistribution;
}) => {
  const appConfigs = useContext(AppConfigContext);

  let degTransmittlGroupID = 0;
  let siteTransmittalGoupID = 0;
  let iceTransmittalGroupID = 0;
  switch (type) {
    case ETransmittalType.DESIGNER:
      degTransmittlGroupID = transmittal?.sendFromProjectCompanyId ?? 0;
      siteTransmittalGoupID = transmittal?.sendToProjectCompanyId ?? 0;
      break;
    case ETransmittalType.SITE:
      siteTransmittalGoupID = transmittal?.sendFromProjectCompanyId ?? 0;
      iceTransmittalGroupID = transmittal?.sendToProjectCompanyId ?? 0;
      break;
    case ETransmittalType.REVIEWER:
      siteTransmittalGoupID = transmittal?.sendFromProjectCompanyId ?? 0;
      iceTransmittalGroupID = transmittal?.sendToProjectCompanyId ?? 0;
      break;
    case ETransmittalType.ICE:
      iceTransmittalGroupID = transmittal?.sendFromProjectCompanyId ?? 0;
      siteTransmittalGoupID = transmittal?.sendToProjectCompanyId ?? 0;
      break;
    default:
      break;
  }

  const isDesignerCompanyRestricted = isRestrictedAccess(degTransmittlGroupID);
  const isSiteCompanyRestricted = isRestrictedAccess(siteTransmittalGoupID);
  const isCheckerCompanyRestricted = isRestrictedAccess(iceTransmittalGroupID);
  const isBelongToDistributionLocal = isBelongToDistribution(distributionData);
  const isReviewerCompanyRestricted = isRestrictedAccess(masterSchedule?.responsibleReviewer?.id ?? -1);

  const isIms = isPermittedAsSystemUser(appConfigs.configs.myPermissions, [
    ERolePermission.IMS_TEAM_OR_SYSTEM_DEVELOPER,
  ]);

  const isGemsSiteUserLocal = isGemsSiteUser();

  if (type === ETransmittalType.DESIGNER) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isBelongToCompany(appConfigs.configs.myCompanies ?? [], degTransmittlGroupID)) return false;
      if (isIms) return false;
    }
    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
    }
    if (isBelongToDistributionLocal) return false;
  }

  if (type === ETransmittalType.SITE) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isGemsSiteUserLocal) return false;
      if (isIms) return false;
    }
    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }

  if (type === ETransmittalType.ICE) {
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isBelongToCompany(appConfigs.configs.myCompanies ?? [], iceTransmittalGroupID)) return false;
      if (isIms) return false;
    }
    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isDesignerCompanyRestricted) return false;
      if (!isCheckerCompanyRestricted) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }
  if (type === ETransmittalType.REVIEWER) {
    if (!isReviewerCompanyRestricted) return false;
    if (transmittal?.status === ETransmittalStatus.DRAFT) {
      if (isBelongToCompany(appConfigs.configs.myCompanies ?? [], masterSchedule?.responsibleDesigner?.id ?? 0))
        return false;
      if (isIms) return false;
    }
    if (transmittal?.status !== ETransmittalStatus.DRAFT) {
      if (!isRestrictedAccess(masterSchedule?.responsibleDesigner?.id)) return false;
      if (!isRestrictedAccess(masterSchedule?.responsibleChecker?.id)) return false;
      if (isBelongToDistributionLocal) return false;
    }
  }

  return true;
};

const isBelongToDistribution = (distributionData?: IProjectTransmittalDistribution) => {
  const appConfigs = useContext(AppConfigContext);

  const isDistributionUser = !!(distributionData?.distributionUser ?? []).find(
    (u) => u.projectUserProfileId === appConfigs.configs.myProjectUserProfile?.id,
  );

  if (isDistributionUser) return true;

  const isDistributionRole = !!(distributionData?.distributionRole ?? []).find(
    (r) => !!r.projectUserProfiles?.find((p) => p.id === appConfigs.configs.myProjectUserProfile?.id),
  );

  if (isDistributionRole) return true;

  return false;
};

export const getTransmittalItemRoute = (transmittalType?: ETransmittalType) => {
  switch (transmittalType) {
    case ETransmittalType.DESIGNER:
      return routes.transmittals.designerTransmittal.item.path;
    case ETransmittalType.SITE:
      return routes.transmittals.siteTransmittal.item.path;
    case ETransmittalType.ICE:
      return routes.transmittals.checkerTransmittal.item.path;
    case ETransmittalType.REVIEWER:
    default:
      return routes.transmittals.lirTransmittal.item.path;
  }
};

export const getLambethReviewerResponseStatusComponentType = (status: string) => {
  switch (status) {
    case EReviewResponseFormStatus.APPROVED:
      return ComponentType.Primary;
    case EReviewResponseFormStatus.APPROVED_WITH_COMMENT:
      return ComponentType.Info;
    case EReviewResponseFormStatus.COMMENTED:
      return ComponentType.Success;
    case EReviewResponseFormStatus.CANCELLED:
    default:
      return ComponentType.Secondary;
  }
};

export const useDefaultValue = (useFormMethods: UseFormReturn) => {
  const [defaultValues, setDefaultValues] = useState<ITransmittalDetailsFormState>({
    [ETransmittalDetailsFormKeys.REMARK]: null,
    [ETransmittalDetailsFormKeys.PURPOSE_OF_ISSUE]: null,
    [ETransmittalDetailsFormKeys.RSE]: [],
    [ETransmittalDetailsFormKeys.CHANGE_T3]: [],
    [ETransmittalDetailsFormKeys.DOCUMENTS]: [],
  });

  useEffect(() => {
    useFormMethods.reset(defaultValues, { keepDirty: true, keepValues: true });
  }, [defaultValues]);

  return { defaultValues, setDefaultValues };
};

export const useTransmittalDetailPageConfirmationModal = (
  onConfirm: (confirmationActionType?: ETransmittalConfirmationActionType) => void,
) => {
  const [confirmationActionType, setConfirmationActionType] = React.useState<ETransmittalConfirmationActionType>();
  const [itemsOfUploadRevisionWithSameFileName, setItemsOfUploadRevisionWithSameFileName] =
    useState<IFormFileDrawingItem[]>();

  const { renderModal: renderConfirmationPortal, openPortal } = useConfirmationModal({
    content: getTransmittalConfirmationActionMessage(confirmationActionType, itemsOfUploadRevisionWithSameFileName),
    onConfirm: () => onConfirm(confirmationActionType),
  });

  const openConfirmationPortal = useCallback(
    (
      e: any,
      type: ETransmittalConfirmationActionType,
      useFormMethods?: UseFormReturn,
      tableIndex?: number,
      rowIndex?: number,
    ) => {
      setConfirmationActionType(type);
      if (
        useFormMethods &&
        (type === ETransmittalConfirmationActionType.save || ETransmittalConfirmationActionType.endorse)
      ) {
        const itemsOfUploadRevisionWithSameFileNameLocal = getItemsOfUploadRevisionWithSameFileName(
          useFormMethods,
          tableIndex,
          rowIndex,
        );
        setItemsOfUploadRevisionWithSameFileName(itemsOfUploadRevisionWithSameFileNameLocal);
        if (
          type === ETransmittalConfirmationActionType.save &&
          itemsOfUploadRevisionWithSameFileNameLocal.length <= 0
        ) {
          onConfirm(type);
          return;
        }
        openPortal(e);
        return;
      }
      openPortal(e);
    },
    [onConfirm],
  );

  return {
    renderConfirmationPortal,
    openConfirmationPortal,
    confirmationActionType,
    setConfirmationActionType,
  };
};

export const getTransmittalConfirmationActionMessage = (
  type?: ETransmittalConfirmationActionType,
  itemsOfUploadRevisionWithSameFileName: IFormFileDrawingItem[] = [],
) => {
  const prefixItemsOfUploadRevisionWithSameFileName =
    itemsOfUploadRevisionWithSameFileName.length > 0
      ? 'The Following transmittal items will be updated with new revision having same file name as prevision revision. '
      : '';
  const suffixItemsOfUploadRevisionWithSameFileName = (
    <>
      {itemsOfUploadRevisionWithSameFileName?.map((s) => (
        <div key={s.form?.projectFile?.uuid}>
          {s.form?.item?.docNo} (Filename: {s.form?.projectFile?.fileName})
        </div>
      ))}
    </>
  );
  switch (type) {
    case ETransmittalConfirmationActionType.save:
      return (
        <div>
          {prefixItemsOfUploadRevisionWithSameFileName}
          Confirm to proceed? <br />
          <br />
          {suffixItemsOfUploadRevisionWithSameFileName}
        </div>
      );
    case ETransmittalConfirmationActionType.delete:
      return 'Confirm to delete transmittal?';
    case ETransmittalConfirmationActionType.endorse:
      return (
        <div>
          {prefixItemsOfUploadRevisionWithSameFileName}
          Confirm to endorse? <br />
          <br />
          {suffixItemsOfUploadRevisionWithSameFileName}
        </div>
      );
    case ETransmittalConfirmationActionType.requestEndorsement:
      return 'Confirm to request endorsement?';
    case ETransmittalConfirmationActionType.releaseForConstruction:
      return 'Confirm to release for construction?';
    case ETransmittalConfirmationActionType.signAndSend:
      return 'Confirm to sign and send?';
    case ETransmittalConfirmationActionType.prepareApproval:
      return 'Confirm to prepare approval?';
    case ETransmittalConfirmationActionType.prepareReview:
      return 'Confirm to prepare review?';
    default:
      return '';
  }
};

export const useShouldShowRemovedFrom = () => {
  const defaultIsHideRemovedFromUse = useUserProfilePreferences<boolean | undefined>(
    EUserProfilePreferenceAttributeName.HIDE_TRANSMITTAL_REMOVED_FROM_USE_ITEM,
    false,
  );
  const [shouldShowRemovedFromUse, setShouldShowRemovedFromUse] = React.useState<boolean>(!defaultIsHideRemovedFromUse);

  return { shouldShowRemovedFromUse, setShouldShowRemovedFromUse };
};
